import { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Activity,
  TrendingUp,
  AlertCircle,
  Wallet,
  Layers,
  DollarSign,
  Target,
  ShoppingBag,
  Coins,
  Receipt,
  Scale,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Trophy,
  Medal,
  ChevronRight,
  Store,
  X,
} from "lucide-react";
import {AnimatePresence, motion } from "framer-motion";
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  Cell,
  XAxis,
  YAxis,
  ComposedChart,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  ReferenceLine
} from "recharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { apiFetch } from "../utils/apiFetch";
import TopProductsCard from './TopProductsCard';
import ProductListModal from './ProductListModal';


import PageWrapper from "../components/PageWrapper";

export default function Dashboard() {
  const chartRef = useRef(null);
  const [data, setData] = useState(null);
  // State untuk menyimpan data toko yang sedang dipilih/aktif di popup
const [selectedStore, setSelectedStore] = useState(null);
const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProgram, setActiveProgram] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [yoyData, setYoyData] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [selectedSpi, setSelectedSpi] = useState(null);
  const [spiData, setSpiData] = useState([]);
  const [deptDataApi, setDeptDataApi] = useState([]);
  const [programData, setProgramData] = useState([]);
  const [brandDataApi, setBrandDataApi] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);

  const [dbData, setDbData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Letakkan ini di bagian atas komponen utama Anda bersama state lainnya
const [selectedDept, setSelectedDept] = useState(null);

// Mengambil data top department secara dinamis sebagai default state awal
const activeDept = selectedDept || deptDataApi[0] || null;

  // Pilihan periode dinamis
  const [currentMonth, setCurrentMonth] = useState("6");    
  const [compareMonth, setCompareMonth] = useState("5");    
  const [lastYearMonth, setLastYearMonth] = useState("6");  
const [analysisDept, setAnalysisDept] = useState(null); // Menyimpan data dept yang sedang dianalisis di popup
  const [summary, setSummary] = useState(null);
  const [activeLine, setActiveLine] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [storeGrowthData, setStoreGrowthData] = useState([]);

  useEffect(() => {
    if (programData.length > 0 && !selectedProgram) {
      setSelectedProgram(programData[0]);
    }
  }, [programData]);

  const formatRupiah = (num) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(num || 0);



    const handleFetchProductData = async () => {
    setIsLoading(true);
    try {
      // Mengirimkan nama departemen secara dinamis ke backend
      const response = await axios.get(`/api/sales/produk-dept-analytics?dept=${activeDept.name}`);
      
      if (response.data.success) {
        setDbData(response.data.data); // simpan hasil array data sql ke state
        setIsModalOpen(true);          // buka modal setelah data siap
      }
    } catch (error) {
      console.error("Gagal memuat produk dari database:", error);
      alert("Gagal memuat data produk, silakan coba lagi.");
    } finally {
      setIsLoading(false);
    }
  };

  const [chartSize, setChartSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (chartRef.current) {
      const { offsetWidth, offsetHeight } = chartRef.current;
      setChartSize({ w: offsetWidth, h: offsetHeight });
    }
  }, [programData]);

  const getMonthFormat = (m, yearOffset = 0) => {
    if (m === "ALL") return "ALL";
    const year = new Date().getFullYear() - yearOffset;
    return `${year}-${String(m).padStart(2, "0")}`;
  };

  const totalRevenue = programData.reduce((acc, cur) => acc + cur.revenue, 0);
  const RADIAN = Math.PI / 180;

  // Tempatkan deklarasi ini sebelum baris return JSX Anda
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: 20, y: 10 },
  visible: { 
    opacity: 1, 
    x: 0, 
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  },
};


  const renderNeedle = (value, total, cx, cy, iR, oR, color) => {
    const angle = 180 * (1 - value / total);
    const length = (iR + 2 * oR) / 3;
    const sin = Math.sin(-RADIAN * angle);
    const cos = Math.sin(-RADIAN * angle);
    const x0 = cx;
    const y0 = cy;
    const x1 = x0 + length * cos;
    const y1 = y0 + length * sin;

    return (
      <>
        <circle cx={x0} cy={y0} r={5} fill={color} />
        <path d={`M${x0},${y0} L${x1},${y1}`} stroke={color} strokeWidth={3} />
      </>
    );
  };

  const needleValue = selectedProgram?.revenue || 0;
const handleStoreClick = async (store) => {
  // 1. Pasang data dasar toko dulu agar popup langsung terbuka (UX terasa responsif)
  setSelectedStore({
    ...store,
    programContributions: [], // Kosongkan dulu selagi loading
    isLoadingContributions: true
  });

  try {
    // DISINI PERBAIKANNYA: Seleraskan menggunakan currentMonth dan format yang sama dengan fetch lainnya
    const bulanParam = typeof currentMonth !== 'undefined' ? getMonthFormat(currentMonth, 0) : 'ALL';

    // 2. Tembak API Backend menggunakan format Query String (?kode_toko=...&bulan=...)
    const response = await fetch(`/api/sales/store-program-contribution?kode_toko=${store.storeCode}&bulan=${bulanParam}`);
    const result = await response.json();

    if (result.success) {
      // 3. Update state store dengan data riil kontribusi program dari database
      setSelectedStore(prev => {
        if (!prev || prev.storeCode !== store.storeCode) return prev;
        return {
          ...prev,
          programContributions: result.data,
          isLoadingContributions: false
        };
      });
    } else {
      console.error("Backend Error:", result.message);
      setSelectedStore(prev => ({ ...prev, isLoadingContributions: false }));
    }
  } catch (error) {
    console.error("Gagal memuat data kontribusi program:", error);
    setSelectedStore(prev => ({ ...prev, isLoadingContributions: false }));
  }
};
  const getMonthLabel = (m, yearOffset = 0) => {
    if (m === "ALL") return "All_Months";
    const year = new Date().getFullYear() - yearOffset;
    const monthName = new Date(0, m - 1).toLocaleString("id-ID", {
      month: "long",
    });
    return `${monthName}_${year}`;
  };

  const fetchProgram = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);

      const res = await apiFetch(`/api/sales/summary-by-program?${params.toString()}`, {
        headers: {
          "x-api-key": "SECRET123456",
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      const formatted = (json.data || []).map((item) => ({
        name: item.program_promo,
        revenue: Number(item.net_revenue || 0),
        gross: Number(item.gross_revenue || 0),
        qty: Number(item.qty || 0),
        trx: Number(item.total_transaksi || 0),
      }));
      setProgramData(formatted);
    } catch (err) {
      console.log("program error:", err);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet([
      {
        Month: currentMonth,
        Target: summary?.target,
        "MTD Sales": summary?.mtd_sales,
        "Ach to Full Moon (%)": summary?.ach_to_full_moon,
        "GAP to Target": summary?.gap_to_target,
        "GAP vs Compare Month": summary?.gap_vs_compare_month,
        "GAP vs Last Year Month": summary?.gap_vs_last_year,
        "Transaction Total": summary?.transaction_total,
        AOV: summary?.aov,
        "Price Point": summary?.price_point,
        QTY: summary?.qty,
        "Basket Size": summary?.basket_size,
      },
    ]);

    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(spiData), "SPI ");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(brandDataApi), "Brand Contribution");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(deptDataApi), "Dept Contribution");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(yoyData), "YoY");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(chartData), "Daily Traffic");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(programData), "Sales by Program Acara");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(file, `Dashboard_Comparison_${getMonthLabel(currentMonth, 0)}.xlsx`);
  };

  const fetchSpi = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);
      params.append("limit", 10);
      params.append("sort_by", "revenue");

      const res = await apiFetch(`/api/sales/summary-spi?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setSpiData((json.data || []).map(item => ({
        name: item.crew,
        revenue: Number(item.net_revenue || 0),
        qty: Number(item.total_qty || 0),
        trx: Number(item.total_transaksi || 0),
        rank: item.ranking,
        photo: item.photo || null,
      })));
    } catch (err) { console.log("spi error:", err); }
  };

const fetchDept = async () => {
  try {
    const token = localStorage.getItem("token");
    const kode_toko = localStorage.getItem("kode_toko"); 
    
    const params = new URLSearchParams();
    params.append("current_bulan", getMonthFormat(currentMonth, 0)); 
    params.append("compare_bulan", getMonthFormat(currentMonth, -1)); 
    params.append("last_year_bulan", getMonthFormat(currentMonth, -12)); 
    params.append("kode_toko", kode_toko);

    const res = await apiFetch(`/api/sales/customer-dept-summary?${params.toString()}`, {
      headers: { 
        "x-api-key": "SECRET123456", 
        Authorization: `Bearer ${token}` 
      },
    });
    
    const json = await res.json();
    console.log("Cek Response API Asli:", json); // Silakan cek di inspect element console browser Anda
    
    // MAPPING DENGAN FALLBACK AMAN (Mendukung format lama & format baru)
   // MAPPING DENGAN FALLBACK AMAN (Mendukung format lama & format baru)
setDeptDataApi((json.data || []).map(item => ({
  name: item.dept,
  value: Number(item.current_net_revenue || item.net_revenue || 0), 
  qty: Number(item.current_total_qty || item.total_qty || 0),     
  trx: Number(item.current_total_transaksi || item.total_transaksi || 0), 
  ranking: Number(item.ranking_dept || item.ranking || 0),
  
  // BARU: Menyimpan data sisa stok akhir dari API summary ke dalam object state React
  // Mendukung nama kolom dari query SQL 'current_stock' atau 'current_stok'
  currentStock: Number(item.current_stock || item.current_stok || 0), 

  // Data Growth MoM & YoY
  growthComparePct: Number(item.growth_sales_vs_compare_pct || 0),
  growthLastYearPct: Number(item.growth_sales_vs_last_year_pct || 0),
  compareValue: Number(item.compare_net_revenue || 0),
  lastYearValue: Number(item.last_year_net_revenue || 0),

  normal_sold: Number(item.normal_sold || 0),
  happy_price_sold: Number(item.happy_price_sold || 0),
  aging_sold: Number(item.aging_sold || 0),

    stok_normal: Number(item.stok_normal || 0),
  stok_aging: Number(item.stok_aging || 0),
  stok_hp: Number(item.stok_hp || 0),
})));
    
  } catch (err) { 
    console.log("dept error:", err); 
  }
};


// =========================================================================
// LOGIKA KALKULASI RUN-RATE DASHBOARD MAL (TARUH DI ATAS RETURN JSX)
// =========================================================================
const hariIni = new Date().getDate();
const totalHariBulanIni = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
const hariBerjalan = hariIni === 0 ? 1 : hariIni;

// 1. Prediksi target permintaan pasar sampai akhir bulan
const marketDemandEst = activeDept ? Math.round((activeDept.qty / hariBerjalan) * totalHariBulanIni) : 0;

// 2. DEFINE SUPPLY CAPACITY: Gabungan barang terjual + sisa stok gudang (mengatasi error no-undef)
const supplyCapacity = activeDept ? (activeDept.qty + (activeDept.currentStock || activeDept.current_stock || 0)) : 0;

// 3. Menghitung koordinat titik silang grafik (E) secara dinamis
const rasioPermintaan = supplyCapacity > 0 ? marketDemandEst / supplyCapacity : 0.5;

// Rentang koordinat sumbu Q (X): efektif berjalan dari 20 sampai 170 (lebar efektif 150)
const eqX = 20 + Math.min(Math.max(rasioPermintaan * 100, 15), 150);

// Rentang koordinat sumbu P (Y): efektif berjalan dari 70 (bawah) ke 15 (atas)
const eqY = 70 - Math.min(Math.max(rasioPermintaan * 35, 10), 50);
// =========================================================================

  const selectedData = yoyData.find((item) => item.month === selectedMonth) || yoyData[yoyData.length - 1];

  const fetchPopularProducts = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);
      params.append("limit", 8);

      const res = await apiFetch(`/api/sales/popular-products?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setPopularProducts(json.data || []);
    } catch (err) { console.log("popular product error:", err); }
  };

const fetchBrand = async () => {
  try {
    const token = localStorage.getItem("token");
    const kode_toko = localStorage.getItem("kode_toko");
    
    // Sesuaikan parameter bulan Anda
    const targetBulan = getMonthFormat(currentMonth, 0); 

    const res = await apiFetch(`/api/sales/summary-brand?current_bulan=${targetBulan}&kode_toko=${kode_toko}`, {
      headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    
    if (json.success && json.data) {
      // Petakan seluruh data database ke objek State React
      setBrandDataApi(json.data.map(item => ({
        name: item.brand || "UNKNOWN",
        share: parseFloat(item.kontribusi) || 0,
        qty: parseInt(item.total_qty) || 0,
        revenue: parseFloat(item.net_revenue) || 0,
        ssrApr: parseFloat(item.ssr_prev) || 0,
        ssrMei: parseFloat(item.ssr_current) || 0,
        // Kalkulasi Growth sederhana atau bisa dari hitungan query
        growth: item.ssr_prev ? (((item.ssr_prev - item.ssr_current) / item.ssr_prev) * 100).toFixed(2) + "%" : "0%"
      })));
    }
  } catch (err) { 
    console.log("brand error:", err); 
  }
};

const fetchYoY = async () => {
    try {
      const params = new URLSearchParams();
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);

      const res = await apiFetch(`/api/sales/chart-yoy-summary?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setYoyData((json.data || []).map(item => ({
        month: item.month,
        thisYear: Number(item.gross_current),
        lastYear: Number(item.gross_last),
        netThis: Number(item.net_current),
        netLast: Number(item.net_last),
        trxThis: Number(item.trx_current),
        trxLast: Number(item.trx_last),
        growth: Number(item.growth_pct),       // YoY GAP
        momGrowth: Number(item.mom_growth_pct) // MoM GAP 🔥 Added
      })));
    } catch (err) { console.log("YoY error:", err); }
  };

  const fetchStoreGrowth = async () => {
  try {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    
    params.append("bulan", getMonthFormat(currentMonth, 0));
    params.append("bulan_banding", getMonthFormat(compareMonth, 0)); 
    // Jika backend membutuhkan tahun lalu secara eksplisit, Anda bisa buka baris di bawah:
    // params.append("bulan_tahun_lalu", getMonthFormat(lastYearMonth, 1));

    const res = await apiFetch(`/api/sales/store-growth-gap?${params.toString()}`, {
      headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    
    setStoreGrowthData((json.data || []).map(item => ({
      storeCode: item.kode_toko,
      storeName: item.nama_toko,
      mtdSales: Number(item.mtd_sales) || 0,
      target: Number(item.target) || 0,
      gapAmount: Number(item.gap_to_target) || 0,
      momGrowthPct: Number(item.mom_growth_pct) || 0,  // 🔥 Growth MoM (%)
      yoyGrowthPct: Number(item.yoy_growth_pct) || 0,  // 🔥 Growth YoY (%)
      achPct: Number(item.achievement_percent) || 0
    })));
  } catch (err) {
    console.log("store growth error:", err);
  }
};


  const fetchChart = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const params = new URLSearchParams();
      params.append("bulan", getMonthFormat(currentMonth, 0));
      params.append("kode_toko", kode_toko);

      const res = await apiFetch(`/api/sales/chart-overview?${params.toString()}`, {
        headers: { "x-api-key": "SECRET123456", Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      
      // 🔥 SINKRONISASI TOTAL ANTARA DATA BACKEND DAN RECHARTS LINE-DATA
      setChartData((json.data || []).map(item => ({
        date: item.date, // Ambil raw date string ("2026-06-01T00:00:00.000Z") agar fungsi split di XAxis berjalan normal
        revenue: Number(item.gross_revenue) || 0,
        net_revenue: Number(item.net_revenue) || 0,                 // ✅ Sesuai dataKey="net_revenue"
        daily_target: Number(item.daily_target) || 0,               // ✅ Sesuai dataKey="daily_target"
        achievement_percent: Number(item.achievement_percent) || 0, // ✅ Sesuai dataKey="achievement_percent"
        transactions: Number(item.total_transaksi) || 0,
      })));
    } catch (err) { 
      console.log("chart error:", err); 
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem("token");
      const kode_toko = localStorage.getItem("kode_toko");
      const url = `/api/sales/summary-monthly?current_bulan=${getMonthFormat(currentMonth, 0)}&compare_bulan=${getMonthFormat(compareMonth, 0)}&last_year_bulan=${getMonthFormat(lastYearMonth, 1)}&kode_toko=${kode_toko}`;

      const res = await apiFetch(url, {
        headers: {
          "x-api-key": "SECRET123456",
          Authorization: `Bearer ${token}`,
        },
      }).then((r) => r.json());

      if (res.success && res.data) {
        setSummary(res.data);
      }
    } catch (err) {
      console.error("fetchSummary error:", err);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchProgram();
    fetchSpi();
    fetchDept();
    fetchBrand();
    fetchPopularProducts();
    fetchChart();
    fetchYoY();
    fetchStoreGrowth();
  }, [currentMonth, compareMonth, lastYearMonth]);

  const cx = chartSize.w * 0.5;
  const cy = chartSize.h * 0.55;

  const renderIndicator = (val, isPercent = false, customPrefix = "") => {
    if (val === undefined || val === null) return <span className="text-gray-400">—</span>;
    const num = Number(val);
    const isPositive = num >= 0;
    
    let formattedVal = "";
    if (isPercent) {
      formattedVal = `${Math.abs(num).toFixed(1)}%`;
    } else {
      formattedVal = Math.abs(num).toLocaleString("id-ID", { maximumFractionDigits: 0 });
    }

    return (
      <span className={`inline-flex items-center font-semibold gap-0.5 px-1.5 py-0.5 rounded-md ${
        isPositive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"
      }`}>
        {isPositive ? <ArrowUpRight size={12} className="stroke-[2.5]" /> : <ArrowDownRight size={12} className="stroke-[2.5]" />}
        {isPositive ? "+" : "-"}{customPrefix}{formattedVal}
      </span>
    );
  };

  const stats = [
    {
      label: "Target Bulanan",
      value: summary ? formatRupiah(summary.target) : "Rp 0",
      icon: Target,
      color: "text-blue-600 bg-gradient-to-br from-blue-100 to-blue-50/50 border-blue-200 shadow-blue-50/50",
      mom: summary ? renderIndicator(summary.growth_sales_vs_compare_pct, true) : "0%",
      yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_to_target, false, "Rp ") : "Rp 0",
      gapLabel: "Gap Target"
    },
    {
      label: "MTD Sales",
      value: summary ? formatRupiah(summary.mtd_sales) : "Rp 0",
      icon: DollarSign,
      color: "text-rose-600 bg-gradient-to-br from-rose-100 to-rose-50/50 border-rose-200 shadow-rose-50/50",
      mom: summary ? renderIndicator(summary.growth_sales_vs_compare_pct, true) : "0%",
      yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_vs_compare_month, false, "Rp ") : "Rp 0",
      gapLabel: "Gap MoM"
    },
    {
      label: "GAP to Target",
      value: summary ? formatRupiah(summary.gap_to_target) : "Rp 0",
      icon: AlertCircle,
      color: summary?.gap_to_target <= 0 
        ? "text-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-50/50 border-emerald-200 shadow-emerald-50/50" 
        : "text-amber-600 bg-gradient-to-br from-amber-100 to-amber-50/50 border-amber-200 shadow-amber-50/50",
      mom: summary ? renderIndicator(summary.growth_sales_vs_compare_pct, true) : "0%",
      yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_to_target, false, "Rp ") : "Rp 0",
      gapLabel: "Gap Target"
    },
    {
      label: "Total Transaksi",
      value: summary ? Number(summary.transaction_total).toLocaleString("id-ID") : "0",
      icon: Receipt,
      color: "text-purple-600 bg-gradient-to-br from-purple-100 to-purple-50/50 border-purple-200 shadow-purple-50/50",
      mom: summary ? renderIndicator((summary.gap_trx_vs_compare / Math.max(summary.transaction_total - summary.gap_trx_vs_compare, 1)) * 100, true) : "0%",
      yoy: summary ? renderIndicator(0, true) : "0%", 
      gap: summary ? renderIndicator(summary.gap_trx_vs_compare, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Average Order Value",
      value: summary ? formatRupiah(summary.aov) : "Rp 0",
      icon: Wallet,
      color: "text-cyan-600 bg-gradient-to-br from-cyan-100 to-cyan-50/50 border-cyan-200 shadow-cyan-50/50",
      mom: summary ? renderIndicator((summary.gap_aov_vs_compare / Math.max(summary.aov - summary.gap_aov_vs_compare, 1)) * 100, true) : "0%",
      yoy: summary ? renderIndicator(0, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_aov_vs_compare, false, "Rp ") : "Rp 0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Price Point",
      value: summary ? formatRupiah(summary.price_point) : "Rp 0",
      icon: Coins,
      color: "text-yellow-600 bg-gradient-to-br from-yellow-100 to-yellow-50/50 border-yellow-200 shadow-yellow-50/50",
      mom: summary ? renderIndicator((summary.gap_price_vs_compare / Math.max(summary.price_point - summary.gap_price_vs_compare, 1)) * 100, true) : "0%",
      yoy: summary ? renderIndicator(0, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_price_vs_compare, false, "Rp ") : "Rp 0",
      gapLabel: "Gap MoM"
    },
    {
      label: "QTY Terjual",
      value: summary ? Number(summary.qty).toLocaleString("id-ID") : "0",
      icon: ShoppingBag,
      color: "text-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-50/50 border-emerald-200 shadow-emerald-50/50",
      mom: summary ? renderIndicator((summary.gap_qty_vs_compare / Math.max(summary.qty - summary.gap_qty_vs_compare, 1)) * 100, true) : "0%",
      yoy: summary ? renderIndicator(0, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_qty_vs_compare, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Basket Size (UPT)",
      value: summary ? Number(summary.basket_size).toFixed(2) : "0",
      icon: Scale,
      color: "text-fuchsia-600 bg-gradient-to-br from-fuchsia-100 to-fuchsia-50/50 border-fuchsia-200 shadow-fuchsia-50/50",
      mom: summary ? renderIndicator((summary.gap_basket_vs_compare / Math.max(summary.basket_size - summary.gap_basket_vs_compare, 0.1)) * 100, true) : "0%",
      yoy: summary ? renderIndicator(0, true) : "0%",
      gap: summary ? renderIndicator(summary.gap_basket_vs_compare, false) : "0",
      gapLabel: "Gap MoM"
    },
    {
      label: "Performance Index",
      value: summary ? `${((Number(summary.mtd_sales) / Math.max(Number(summary.target), 1)) * 100).toFixed(0)}%` : "0%",
      icon: Activity,
      color: "text-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-50/50 border-indigo-200 shadow-indigo-50/50",
      mom: summary ? renderIndicator(summary.growth_sales_vs_compare_pct, true) : "0%",
      yoy: summary ? renderIndicator(summary.growth_sales_vs_last_year_pct, true) : "0%",
      gap: summary ? renderIndicator(summary.ach_to_full_moon, true) : "0%",
      gapLabel: "Ach. Rate"
    }
  ];

  const COLORS = ["#4f46e5", "#2563eb", "#0891b2", "#059669", "#d97706", "#dc2626"];

  return (
    <PageWrapper>
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">Executive Retail Analytics</h1>
          <p className="text-xs text-gray-500">Kompilasi komparasi performa multi-dimensi penjualan toko</p>
        </div>

        {/* CONTROLLER SECTION */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full md:w-auto items-end">
          <div className="flex flex-col w-full sm:w-auto">
            <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Bulan Berjalan (A)</span>
            <select
              value={currentMonth}
              onChange={(e) => setCurrentMonth(e.target.value)}
              className="w-full sm:w-auto border border-gray-200 rounded-xl px-3 py-1.5 text-xs bg-white font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("en-US", { month: "long" })} 2026
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-full sm:w-auto">
            <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Bulan Perbandingan (B)</span>
            <select
              value={compareMonth}
              onChange={(e) => setCompareMonth(e.target.value)}
              className="w-full sm:w-auto border border-gray-200 rounded-xl px-3 py-1.5 text-xs bg-white font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("en-US", { month: "long" })} 2026
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-full sm:w-auto">
            <span className="text-[10px] font-bold text-gray-400 uppercase mb-1 tracking-wider">Bulan 1 Tahun Lalu (C)</span>
            <select
              value={lastYearMonth}
              onChange={(e) => setLastYearMonth(e.target.value)}
              className="w-full sm:w-auto border border-gray-200 rounded-xl px-3 py-1.5 text-xs bg-white font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i + 1}>
                  {new Date(0, i).toLocaleString("en-US", { month: "long" })} 2025
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={exportToExcel}
            className="w-full sm:w-auto px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl transition shadow-md shadow-indigo-100 active:scale-95 h-[34px] flex items-center justify-center gap-1.5"
          >
            Export Comprehensive Report
          </button>
        </div>
      </div>

      {/* THE MODERN & UNIFIED STATS CARD GRID WITH SMOOTH MOTION ANIMS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4, scale: 1.02 }}
              transition={{ duration: 0.35, ease: "easeOut", delay: i * 0.02 }}
              className="bg-white rounded-xl p-3 border border-gray-200/80 shadow-sm shadow-gray-100/50 hover:shadow-md hover:border-gray-300 transition-all duration-200 flex flex-col justify-between group relative overflow-hidden"
            >
              {/* Top Meta Line */}
              <div className="flex items-center justify-between gap-1 w-full mb-2">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider truncate block">
                  {s.label}
                </span>
                <div className={`p-1.5 rounded-lg border shadow-inner shrink-0 group-hover:rotate-6 transition duration-200 ${s.color}`}>
                  <Icon size={13} className="stroke-[2.5]" />
                </div>
              </div>

              {/* Core Metric Value */}
              <div className="my-1.5">
                <h3 className="text-sm md:text-base xl:text-[16px] font-semibold text-gray-900 tracking-tight break-words">
                  {s.value}
                </h3>
              </div>

              {/* Layout Data Multi-dimensi: MoM, YoY, Gap */}
              <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-1 text-[10px] text-gray-500">
                <div className="flex justify-between items-center h-5">
                  <span>MoM:</span>
                  <span className="font-medium">{s.mom}</span>
                </div>
                <div className="flex justify-between items-center h-5">
                  <span>YoY:</span>
                  <span className="font-medium">{s.yoy}</span>
                </div>
                <div className="flex justify-between items-center border-t border-dashed border-gray-100 pt-1 mt-0.5 h-6">
                  <span className="text-[9px] text-gray-400">{s.gapLabel}:</span>
                  <span className="font-medium text-right break-all">{s.gap}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* MAIN GRID CHART GRAPHICS WITH MOTION ENTRANCE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        {/* LEFT - LINE CHART */}
      {/* DAILY PERFORMANCE OVERVIEW (UPGRADED EXECUTIVE VERSION) */}
{/* DAILY PERFORMANCE OVERVIEW (REVENUE VS TARGET) */}
<motion.div 
  initial={{ opacity: 0, scale: 0.98 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.4, delay: 0.2 }}
  className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
>
  <div>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
      <div>
        <h3 className="text-sm font-bold text-gray-800 tracking-wide flex items-center gap-2">
          <span>Daily Revenue vs Target Overview</span>
          <span className="bg-indigo-50 text-indigo-700 text-[10px] font-medium px-2 py-0.5 rounded-full border border-indigo-100">
            Target Tracking
          </span>
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Analisis komparasi omzet bersih (Net Revenue) terhadap target harian beserta capaian persentase keberhasilan (*achievement*).
        </p>
      </div>

      {/* Custom Legend Premium */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100/80 self-start sm:self-center">
        <div className="flex items-center gap-1.5 cursor-pointer" onMouseEnter={() => setActiveLine("net_revenue")} onMouseLeave={() => setActiveLine(null)}>
          <span className={`w-2.5 h-2.5 rounded-full bg-[#4f46e5] transition-transform ${activeLine === 'net_revenue' ? 'scale-125' : ''}`} />
          <span className={activeLine === 'net_revenue' ? 'font-bold text-indigo-600' : ''}>Net Revenue</span>
        </div>
        <div className="flex items-center gap-1.5 cursor-pointer" onMouseEnter={() => setActiveLine("daily_target")} onMouseLeave={() => setActiveLine(null)}>
          <span className={`w-2.5 h-2.5 rounded-full bg-[#94a3b8] transition-transform ${activeLine === 'daily_target' ? 'scale-125' : ''}`} />
          <span className={activeLine === 'daily_target' ? 'font-bold text-slate-600' : ''}>Target</span>
        </div>
        <div className="flex items-center gap-1.5 cursor-pointer" onMouseEnter={() => setActiveLine("achievement_percent")} onMouseLeave={() => setActiveLine(null)}>
          <span className={`w-2.5 h-2.5 rounded-full bg-[#10b981] transition-transform ${activeLine === 'achievement_percent' ? 'scale-125' : ''}`} />
          <span className={activeLine === 'achievement_percent' ? 'font-bold text-emerald-600' : ''}>% Achieved</span>
        </div>
      </div>
    </div>
  </div>

  {/* Chart Area */}
  <div className="h-[320px] w-full">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart 
        data={chartData} // Pastikan state chartData menampung response baru
        margin={{ top: 10, right: -5, left: -20, bottom: 0 }}
      >
        <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
        
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
          tickFormatter={(v) => {
            if (!v) return v;
            const parts = v.split('T')[0].split('-');
            if (parts.length < 3) return v;
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]}`;
          }}
          axisLine={false}
          tickLine={false}
        />
        
        {/* Sumbu Kiri: Nominal Finansial (Revenue & Target) */}
        <YAxis 
          yAxisId="left" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : v}
        />
        
        {/* Sumbu Kanan: Persentase Pencapaian Target */}
        <YAxis 
          yAxisId="right" 
          orientation="right" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => `${v}%`}
        />

        {/* CUSTOM DETAILED TOOLTIP FOR TARGET MANAGEMENT */}
        <Tooltip
          cursor={{ stroke: "#e2e8f0", strokeWidth: 1.5, strokeDasharray: "4 4" }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              const formattedDate = (() => {
                if (!label) return label;
                const parts = label.split('T')[0].split('-');
                if (parts.length < 3) return label;
                const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
                return `${parts[2]} ${months[parseInt(parts[1], 10) - 1]} ${parts[0]}`;
              })();

              const ach = Number(data.achievement_percent || 0);

              return (
                <div className="bg-white/95 backdrop-blur-md p-3.5 rounded-xl border border-gray-200 shadow-xl min-w-[220px] text-[11px]">
                  <p className="font-bold text-gray-800 border-b border-gray-100 pb-1.5 mb-2 text-xs text-center">
                    {formattedDate}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#4f46e5]" /> Omzet Bersih (Net):
                      </span>
                      <span className="font-bold text-gray-900">
                        Rp {Number(data.net_revenue || 0).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#94a3b8]" /> Target Harian:
                      </span>
                      <span className="font-semibold text-gray-600">
                        Rp {Number(data.daily_target || 0).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-4 border-t border-dotted border-gray-100 pt-1.5">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#10b981]" /> Target Achievement:
                      </span>
                      <span className={`font-bold px-1.5 py-0.5 rounded-md ${ach >= 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {ach.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />

        {/* Line Net Revenue */}
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="net_revenue"
          name="Net Revenue"
          dot={false}
          activeDot={{ r: 5, strokeWidth: 1, fill: "#4f46e5" }}
          stroke="#4f46e5"
          strokeWidth={activeLine === "net_revenue" ? 3.5 : 2.5}
          opacity={activeLine && activeLine !== "net_revenue" ? 0.3 : 1}
        />
        
        {/* Line Target Harian */}
        <Line 
          yAxisId="left" 
          type="monotone" 
          dataKey="daily_target" 
          name="Target Harian"
          dot={false} 
          activeDot={{ r: 5, strokeWidth: 1, fill: "#94a3b8" }}
          stroke="#94a3b8" 
          strokeWidth={activeLine === "daily_target" ? 3.5 : 2.5}
          strokeDasharray="4 4"
          opacity={activeLine && activeLine !== "daily_target" ? 0.3 : 1}
        />
        
        {/* Line Achievement Rate */}
        <Line 
          yAxisId="right" 
          type="monotone" 
          dataKey="achievement_percent" 
          name="% Achievement"
          dot={false} 
          activeDot={{ r: 5, strokeWidth: 1, fill: "#10b981" }}
          stroke="#10b981" 
          strokeWidth={activeLine === "achievement_percent" ? 3.5 : 2.5}
          opacity={activeLine && activeLine !== "achievement_percent" ? 0.3 : 1}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
</motion.div>



        {/* RIGHT - YoY CHART (RE-ENGINEERED FOR GAP & GROWTH VISUALIZATION) */}
       {/* RIGHT - YoY & MoM PERFORMANCE CHART */}
       {/* RIGHT - YoY & MoM PERFORMANCE CHART (UPGRADED EXECUTIVE VERSION) */}
<motion.div 
  initial={{ opacity: 0, scale: 0.98 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.4, delay: 0.25 }}
  className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition relative flex flex-col justify-between"
>
  <div>
    <div className="flex items-start justify-between mb-2">
      <div>
        <h3 className="text-sm font-bold text-gray-800 tracking-wide flex items-center gap-2">
          <span>YoY & MoM Growth Gap Analysis</span>
          <span className="bg-indigo-50 text-indigo-700 text-[10px] font-medium px-2 py-0.5 rounded-full border border-indigo-100">
            Dinamis
          </span>
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Komparasi performa omzet tahunan, bulanan, volume transaksi, beserta margin kesehatan pertumbuhan perusahaan.
        </p>
      </div>
      
      {/* Dynamic Floating Highlight Badge */}
      <div className="flex gap-2 shrink-0">
        <div className="bg-slate-50 border border-slate-100 shadow-sm rounded-xl px-3 py-1.5 text-right min-w-[75px]">
          <div className="text-gray-400 font-semibold text-[9px] uppercase tracking-wider">YoY {selectedData?.month ? selectedData.month.split('-')[1] : "Tren"}</div>
          <div className={`font-bold text-xs mt-0.5 flex items-center justify-end gap-0.5 ${(selectedData?.growth || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {(selectedData?.growth || 0) >= 0 ? "▲" : "▼"} {Math.abs(selectedData?.growth || 0).toFixed(1)}%
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-100 shadow-sm rounded-xl px-3 py-1.5 text-right min-w-[75px]">
          <div className="text-gray-400 font-semibold text-[9px] uppercase tracking-wider">MoM {selectedData?.month ? selectedData.month.split('-')[1] : "Tren"}</div>
          <div className={`font-bold text-xs mt-0.5 flex items-center justify-end gap-0.5 ${(selectedData?.momGrowth || 0) >= 0 ? "text-cyan-600" : "text-amber-600"}`}>
            {(selectedData?.momGrowth || 0) >= 0 ? "▲" : "▼"} {Math.abs(selectedData?.momGrowth || 0).toFixed(1)}%
          </div>
        </div>
      </div>
    </div>

    {/* Info Ringkas Tren */}
    <div className="flex items-center gap-4 border-b border-gray-100 pb-3 mb-4 text-[11px] text-gray-500">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-[#4f46e5]" />
        <span>Tahun Ini (Gross)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-[#cbd5e1]" />
        <span>Tahun Lalu (Gross)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-0.5 bg-[#10b981]" />
        <span className="font-medium text-emerald-600">YoY Rate (%)</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-0.5 border-t-2 border-dashed border-[#06b6d4]" />
        <span className="font-medium text-cyan-600">MoM Rate (%)</span>
      </div>
    </div>
  </div>

  {/* Chart Container */}
  <div className="h-[300px] w-100">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={yoyData}
        margin={{ top: 10, right: -5, left: -20, bottom: 0 }}
        onClick={(e) => { 
          if (e && e.activeLabel) {
            setSelectedMonth(e.activeLabel);
          }
        }}
      >
        <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
        
        <XAxis 
          dataKey="month" 
          tickFormatter={(v) => {
            const parts = v.split('-');
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
            return parts[1] ? months[parseInt(parts[1], 10) - 1] : v;
          }}
          tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }} 
          axisLine={false} 
          tickLine={false} 
        />
        
        {/* Sumbu Kiri: Keuangan Finansial */}
        <YAxis 
          yAxisId="left" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => v >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : `${(v / 1e6).toFixed(0)}M`}
        />
        
        {/* Sumbu Kanan: Persentase Pertumbuhan */}
        <YAxis 
          yAxisId="right" 
          orientation="right" 
          tick={{ fontSize: 10, fill: "#94a3b8" }} 
          axisLine={false} 
          tickLine={false} 
          tickFormatter={(v) => `${v}%`} 
        />
        
        {/* 🔥 HIGHLY DETAILED CUSTOM TOOLTIP */}
        <Tooltip
          cursor={{ fill: '#f8fafc' }}
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              const formattedLabel = (() => {
                const parts = label.split('-');
                const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
                return parts[1] ? `${months[parseInt(parts[1], 10) - 1]} ${parts[0]}` : label;
              })();

              return (
                <div className="bg-white/95 backdrop-blur-md p-4 rounded-xl border border-gray-200 shadow-xl max-w-xs text-[11px]">
                  <p className="font-bold text-gray-800 border-b border-gray-100 pb-1.5 mb-2 text-xs text-center">{formattedLabel}</p>
                  
                  {/* Bagian Perbandingan Nominal */}
                  <div className="space-y-1.5 mb-2.5">
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500 font-medium">Gross Sales (This Year):</span>
                      <span className="font-bold text-gray-900">Rp {data.thisYear.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500">Gross Sales (Last Year):</span>
                      <span className="font-semibold text-gray-600">Rp {data.lastYear.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between gap-6 border-t border-dotted border-gray-100 pt-1">
                      <span className="text-gray-500 font-medium">Net Sales (This Year):</span>
                      <span className="font-bold text-indigo-600">Rp {data.netThis.toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                      <span className="text-gray-500">Total Transaksi (Trx):</span>
                      <span className="font-semibold text-gray-700">{data.trxThis.toLocaleString('id-ID')} trx</span>
                    </div>
                  </div>

                  {/* Bagian Skor Persentase Indikator */}
                  <div className="grid grid-cols-2 gap-2 border-t border-gray-100 pt-2 text-center">
                    <div className={`p-1.5 rounded-lg ${data.growth >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                      <div className="text-[9px] font-medium opacity-80">YoY Growth</div>
                      <div className="font-bold text-xs">{data.growth >= 0 ? "▲" : "▼"} {data.growth.toFixed(1)}%</div>
                    </div>
                    <div className={`p-1.5 rounded-lg ${data.momGrowth >= 0 ? "bg-cyan-50 text-cyan-700" : "bg-amber-50 text-amber-700"}`}>
                      <div className="text-[9px] font-medium opacity-80">MoM Growth</div>
                      <div className="font-bold text-xs">{data.momGrowth >= 0 ? "▲" : "▼"} {data.momGrowth.toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />
        
        {/* Batang Data Grafik */}
        <Bar yAxisId="left" dataKey="lastYear" fill="#cbd5e1" radius={[3, 3, 0, 0]} maxBarSize={16} opacity={0.85} />
        <Bar yAxisId="left" dataKey="thisYear" fill="#4f46e5" radius={[3, 3, 0, 0]} maxBarSize={16} />
        
        {/* Garis Dasar Nol Persen */}
        <ReferenceLine yAxisId="right" y={0} stroke="#e2e8f0" strokeWidth={1} />
        
        {/* Garis Tren Pertumbuhan */}
        <Line yAxisId="right" type="monotone" dataKey="growth" stroke="#10b981" strokeWidth={3} dot={{ r: 2.5, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 4 }} />
        <Line yAxisId="right" type="monotone" dataKey="momGrowth" stroke="#06b6d4" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2.5, strokeWidth: 1, fill: "#fff" }} activeDot={{ r: 4 }} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
</motion.div>


<motion.div 
  initial={{ opacity: 0, scale: 0.98 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.4, delay: 0.25 }}
  className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all duration-300 relative flex flex-col justify-between gap-6 overflow-hidden"
>
  {/* Dekorasi Aksen Latar Belakang - Disamakan dengan yang lain */}
  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-indigo-50/40 to-transparent rounded-bl-full pointer-events-none" />

  {/* SECTION 1: KEPALA GRAFIK & PERSENTASE TOTAL */}
  <div className="relative z-10">
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
      <div>
        <h3 className="text-sm font-bold text-gray-800 tracking-wide flex items-center gap-2">
          <span>Brand Share & Revenue Analysis</span>
          <span className="bg-indigo-50 text-indigo-700 text-[10px] font-medium px-2 py-0.5 rounded-full border border-indigo-100">
            Market Share
          </span>
        </h3>
        <p className="text-[11px] text-gray-400 mt-0.5">
          Visualisasi kontribusi omzet netto horizontal dan rasio perputaran unit per brand.
        </p>
      </div>
      
      {/* Dynamic Floating Highlight Badge Premium */}
      <div className="flex gap-2 shrink-0 self-start sm:self-center">
        <div className="bg-slate-50 border border-slate-100/80 shadow-sm rounded-xl px-3 py-1.5 text-right min-w-[80px]">
          <div className="text-gray-400 font-semibold text-[9px] uppercase tracking-wider">Total Brand</div>
          <div className="font-bold text-xs mt-0.5 text-indigo-600 flex items-center justify-end gap-0.5">
            {brandDataApi.length} <span className="text-[9px] text-gray-400 font-normal">items</span>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-100/80 shadow-sm rounded-xl px-3 py-1.5 text-right min-w-[80px]">
          <div className="text-gray-400 font-semibold text-[9px] uppercase tracking-wider">Top Share</div>
          <div className="font-bold text-xs mt-0.5 text-emerald-600 flex items-center justify-end gap-0.5">
            {brandDataApi[0]?.share || 0}%
          </div>
        </div>
      </div>
    </div>

    {/* Custom Legend - Senada dengan Legend Chart Lain */}
    <div className="flex items-center gap-4 border-b border-gray-100 pb-3 mb-4 text-[11px] text-gray-500">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#4f46e5]" />
        <span>% Kontribusi Sales</span>
      </div>
      <div className="flex items-center gap-1.5 text-gray-200">|</div>
      <div className="text-[10px] text-gray-400 italic">
        *Urutan berdasarkan dominasi kontribusi omzet tertinggi
      </div>
    </div>

    {/* GRAFIK HORIZONTAL BRAND SHARE (UPGRADED SENADA 100%) */}
    <div className="h-[220px] w-full mt-2">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={brandDataApi.length > 0 ? brandDataApi.slice(0, 8) : [{ name: 'No Data', share: 0 }]}
          margin={{ top: 5, right: 10, left: -25, bottom: 5 }}
        >
          {/* Garis Grid - Warna & Dash disamakan (#f1f5f9) */}
          <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} vertical={true} />
          
          <XAxis type="number" domain={[0, 'dataMax + 5']} hide />
          
          <YAxis 
            dataKey="name" 
            type="category" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fontSize: 10, fill: "#64748b", fontWeight: 500 }}
          />

          {/* PREMIUM WHITE GLASSMORPHISM TOOLTIP */}
          <Tooltip
            cursor={{ fill: '#f1f5f9/60' }}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="bg-white/95 backdrop-blur-md p-3 rounded-xl border border-gray-200 shadow-xl min-w-[200px] text-[11px]">
                    <p className="font-bold text-gray-800 border-b border-gray-100 pb-1.5 mb-2 text-xs text-center">
                      {data.name}
                    </p>
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-500 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-[#4f46e5]" /> Market Share:
                        </span>
                        <span className="font-bold text-indigo-600">
                          {data.share}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-4">
                        <span className="text-gray-500 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-slate-400" /> Total Revenue:
                        </span>
                        <span className="font-semibold text-gray-700">
                          Rp {Number(data.revenue || 0).toLocaleString('id-ID')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />

          {/* Bar Styled Clean & Solid Premium */}
          <Bar 
            dataKey="share" 
            fill="url(#brandPremiumGradient)" 
            radius={[0, 4, 4, 0]} 
            barSize={12}
            background={{ fill: '#f8fafc', radius: 4 }}
          >
            <defs>
              <linearGradient id="brandPremiumGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#4f46e5" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0.85} />
              </linearGradient>
            </defs>
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>

  {/* SECTION 2: INDIKATOR DATA TABEL LEDGER */}
  <div className="border-t border-gray-100 pt-4 relative z-10">
    <div className="flex items-center justify-between mb-3">
      <div>
        <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
          <span className="w-1.5 h-3 bg-indigo-600 rounded-full inline-block" />
          Brand Performance Ledger
        </h4>
        <p className="text-[10px] text-gray-400">Rincian komparasi SSR mingguan dan akselerasi margin netto</p>
      </div>
    </div>

    <div className="overflow-x-auto border border-gray-100 rounded-xl shadow-inner max-h-[170px] scrollbar-thin">
      <table className="w-full text-left text-[11px] border-collapse bg-white">
        <thead>
          <tr className="bg-slate-50 border-b border-gray-100 text-[10px] text-gray-500 font-bold uppercase tracking-wider sticky top-0 z-10">
            <th className="py-2.5 px-3 bg-slate-50">Brand</th>
            <th className="py-2.5 px-2 text-right">SSR Lalu</th>
            <th className="py-2.5 px-2 text-right">SSR Kini</th>
            <th className="py-2.5 px-2 text-right">Rev Netto</th>
            <th className="py-2.5 px-3 text-right bg-indigo-50/40 text-indigo-700">% Cont.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 font-medium text-gray-700">
          {brandDataApi.length > 0 ? (
            brandDataApi.map((item, index) => (
              <motion.tr 
                key={index}
                // UTAMA: Ketika baris brand di klik, set state brand yang dipilih
                onClick={() => setSelectedBrand(item.name)}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.03 }}
                whileHover={{ backgroundColor: "#f8fafc", scale: 1.002 }}
                className="hover:bg-slate-50/80 transition-all duration-150"
              >
                <td className="py-2 px-3 font-semibold text-gray-900 border-r border-slate-100/50 truncate max-w-[110px]">
                  {item.name}
                </td>
                <td className="py-2 px-2 text-right text-gray-400">{item.ssrApr || "-"}</td>
                <td className="py-2 px-2 text-right font-semibold text-gray-800">{item.ssrMei || "-"}</td>
                <td className="py-2 px-2 text-right font-bold text-slate-600 text-[10px]">
                  Rp {item.revenue.toLocaleString('id-ID')}
                </td>
                <td className="py-2 px-3 text-right bg-indigo-50/10 font-bold text-indigo-600">
                  {item.share}%
                </td>
              </motion.tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center py-8 text-gray-400 italic">
                Tidak ada data penjualan brand pada periode ini.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
</motion.div>

<TopProductsCard 
          selectedBrand={selectedBrand} 
          setSelectedBrand={setSelectedBrand} 
        />




{/* CARD LIST MULTI-DIMENSIONAL GROWTH & GAP PER TOKO */}


 
      </div>

     <div className="mt-6 w-full">
  {/* HEADER UTAMA SEGMEN */}
  <div className="mt-6 w-full bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
  {/* HEADER UTAMA SEGMEN (Sekarang di dalam kartu bbrand ersama layout) */}
  <div className="flex items-center justify-between mb-5 px-0.5">
    <div>

      <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
          <span className="w-1.5 h-3 bg-indigo-600 rounded-full inline-block" />
         Department Performance & Product Lifecycle Analytics
        </h4>
     
      <p className="text-[10px] text-gray-400 mt-0.5">
        Klik pada salah satu kartu departemen untuk meninjau detail lini pelacakan suplai produk secara real-time.
      </p>
    </div>
  </div>

  {/* LAYOUT DUA KOLOM (MASTER - DETAIL) */}
  <div className="flex flex-col lg:flex-row gap-5 items-start w-full">
    
    {/* KOLOM KIRI: GRID KARTU DEPARTEMEN (MASTER) */}
    <div className="w-full lg:w-[75%] shrink-0">
      <AnimatePresence mode="popLayout">
        {/* Grid responsif yang dinamis menyesuaikan container kiri */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3.5 w-full pt-1 pb-1"
        >
          {deptDataApi.length > 0 ? (
            deptDataApi.map((dept, index) => {
              const isTop3 = index < 3;
              const isSelected = activeDept?.name === dept.name;
              
              const badgeBg = isTop3
                ? index === 0
                  ? "bg-amber-50 text-amber-700 border-amber-100"
                  : index === 1
                  ? "bg-slate-100 text-slate-700 border-slate-200"
                  : "bg-orange-50 text-orange-700 border-orange-100"
                : "bg-gray-50 text-gray-500 border-gray-100";

              return (
                <motion.div
                  key={dept.name}
                  layout
                  variants={cardVariants}
                  onClick={() => {
  setSelectedDept(dept); // Tetap merubah panel history lifecycle kanan
  setAnalysisDept(dept); // BARU: Memicu popup analisa modal muncul
}}
                  whileHover={{ 
                    y: -4, 
                    scale: 1.01,
                    boxShadow: "0 10px 20px -10px rgba(79, 70, 229, 0.15)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full cursor-pointer bg-white border rounded-xl p-3.5 flex flex-col justify-between gap-3 relative overflow-hidden transition-all duration-200 ${
                    isSelected 
                      ? "border-indigo-600 ring-2 ring-indigo-600/10 shadow-sm shadow-indigo-600/5" 
                      : "border-gray-200/90 hover:border-indigo-300"
                  }`}
                >
                  {index === 0 && (
                    <div className="absolute -top-6 -right-6 w-16 h-16 bg-amber-500/5 rounded-full blur-xl pointer-events-none" />
                  )}

                  {/* BARIS ATAS */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="truncate w-full">
                      <span className="text-[9px] text-gray-400 font-semibold block">Dept</span>
                      <h5 className="font-bold text-xs text-gray-800 truncate pr-1" title={dept.name}>
                        {dept.name}
                      </h5>
                    </div>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md border shrink-0 ${badgeBg}`}>
                      #{dept.ranking || index + 1}
                    </span>
                  </div>

                  {/* BARIS TENGAH */}
                  <div className="my-0.5">
                    <span className="text-[9px] text-gray-400 block font-medium">Net Revenue</span>
                    <div className="text-sm font-bold text-slate-800 flex items-baseline gap-0.5">
                      <span className="text-[10px] font-semibold text-slate-500">Rp</span> 
                      {Number(dept.value || 0).toLocaleString('id-ID')}
                    </div>
                  </div>

                  {/* BARIS BAWAH */}
                  {/* BARIS BAWAH */}
<div className="border-t border-gray-100 pt-2 flex flex-col gap-1.5">
  {/* Volume Penjualan & Transaksi */}
  <div className="flex items-center justify-between text-[10px]">
    <div className="text-gray-400 font-medium">Vol Unit / Trx</div>
    <div className="font-bold text-gray-700">
      {dept.qty.toLocaleString('id-ID')} <span className="text-[9px] text-gray-400 font-normal">pcs</span>
      <span className="text-gray-300 mx-1">|</span>
      {dept.trx.toLocaleString('id-ID')} <span className="text-[9px] text-gray-400 font-normal">tx</span>
    </div>
  </div>

  {/* BARU: Informasi Live Cap & Est. Demand per Departemen */}
  {(() => {
    // Hitung Kapasitas Suplai Real (Menggunakan rumus Skenario A yang kita sepakati)
    const itemCap = dept.qty + Number(dept.currentStock || dept.current_stock || 0);

    // Hitung Estimasi Permintaan Run-Rate Bulanan
    const targetHari = totalHariBulanIni || 30;
    const hariAktif = hariBerjalan || new Date().getDate() || 1;
    const itemEstDemand = Math.round((dept.qty / hariAktif) * targetHari);
    
    // Tentukan status kesehatan inventori untuk styling border tipis
    const isShortage = itemEstDemand > itemCap;

    return (
      <>
        <div className="flex items-center justify-between text-[10px] bg-slate-50/70 p-1 rounded border border-slate-100/50 my-0.5">
          <div className="flex flex-col">
            <span className="text-[7px] text-gray-400 font-semibold uppercase tracking-wide">Cap</span>
            <span className="font-bold text-indigo-600">{itemCap.toLocaleString('id-ID')} <span className="text-[8px] font-normal text-gray-400">pcs</span></span>
          </div>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex flex-col text-right">
            <span className="text-[7px] text-gray-400 font-semibold uppercase tracking-wide">Est. Demand</span>
            <span className={`font-bold ${isShortage ? 'text-rose-600' : 'text-amber-600'}`}>
              {itemEstDemand.toLocaleString('id-ID')} <span className="text-[8px] font-normal text-gray-400">pcs</span>
            </span>
          </div>
        </div>
      </>
    );
  })()}

  {/* Growth MoM */}
  <div className="flex items-center justify-between text-[10px]">
    <div className="text-gray-400 font-medium">MoM Growth</div>
    <div className={`font-semibold flex items-center gap-0.5 ${dept.growthComparePct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
      <motion.span
        animate={{ y: dept.growthComparePct >= 0 ? [1, -1, 1] : [-1, 1, -1] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        className="text-[8px]"
      >
        {dept.growthComparePct >= 0 ? '▲' : '▼'}
      </motion.span>
      <span>{Math.abs(dept.growthComparePct || 0).toFixed(1)}%</span>
    </div>
  </div>
</div>
                </motion.div>
              );
            })
          ) : (
            <motion.div 
              layout
              className="col-span-full text-center py-8 border border-dashed border-gray-200 rounded-xl text-gray-400 italic text-xs bg-gray-50/50"
            >
              Tidak ditemukan data matrik departemen pada periode ini.
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>

    {/* KOLOM KANAN: PANEL HISTORY PERJALANAN & LIFECYCLE (DETAIL) */}
   <div className="w-full lg:w-[35%] sticky top-4">
    {activeDept ? (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full bg-white border border-gray-100 rounded-xl p-4 shadow-sm"
      >
        {/* Header Detail Card */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4">
          <div>
            <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
              Product Lifecycle History
            </span>
            <h4 className="text-sm font-bold text-slate-800 mt-1">
              {activeDept.name}
            </h4>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-gray-400 font-medium">Current Month Vol</p>
            <p className="text-xs font-bold text-slate-700">{activeDept.qty.toLocaleString('id-ID')} pcs</p>
          </div>
        </div>

        {/* KOMPONEN CHART INFORMATIF (MINI CHART DENGAN SVG) */}
        <div className="mb-4 bg-gradient-to-b from-slate-50 to-white border border-gray-100 p-3 rounded-lg">
          <p className="text-[10px] font-bold text-gray-700 mb-2 flex items-center justify-between">
            <span>Volume Alur Distribusi Berjalan</span>
            <span className="text-indigo-600 text-[9px]">Target Efisiensi: 94%</span>
          </p>
          
          {/* Simulasi Chart Batang Responsif */}
          <div className="h-20 w-full flex items-end justify-between gap-3 pt-4 px-2 relative">
            {/* Garis Grid Pembantu */}
            <div className="absolute inset-x-0 top-4 border-t border-dashed border-gray-200 w-full z-0" />
            <div className="absolute inset-x-0 top-12 border-t border-dashed border-gray-200 w-full z-0" />
            
            {/* Bar 1: Produksi */}
            <div className="w-full flex flex-col items-center gap-1.5 z-10">
              <span className="text-[8px] font-bold text-slate-500">1.2K</span>
              <div className="w-full bg-slate-300 rounded-t h-12 transition-all duration-500" />
              <span className="text-[8px] text-gray-400 font-medium truncate w-full text-center">Prod</span>
            </div>
            {/* Bar 2: Distribusi */}
            <div className="w-full flex flex-col items-center gap-1.5 z-10">
              <span className="text-[8px] font-bold text-sky-600">1.1K</span>
              <div className="w-full bg-sky-500 rounded-t h-11 transition-all duration-500" />
              <span className="text-[8px] text-gray-400 font-medium truncate w-full text-center">Dist</span>
            </div>
            {/* Bar 3: Sampai Toko */}
            <div className="w-full flex flex-col items-center gap-1.5 z-10">
              <span className="text-[8px] font-bold text-amber-600">940</span>
              <div className="w-full bg-amber-500 rounded-t h-9 transition-all duration-500" />
              <span className="text-[8px] text-gray-400 font-medium truncate w-full text-center">Toko</span>
            </div>
            {/* Bar 4: Terjual */}
            <div className="w-full flex flex-col items-center gap-1.5 z-10">
              <span className="text-[8px] font-bold text-indigo-600">{activeDept.qty}</span>
              <div 
                style={{ height: `${Math.min(Math.max((activeDept.qty / 1200) * 48, 8), 48)}px` }}
                className="w-full bg-indigo-600 rounded-t transition-all duration-500" 
              />
              <span className="text-[8px] text-indigo-600 font-bold truncate w-full text-center">Sold</span>
            </div>
          </div>
        </div>

        {/* DINAMIS: GRAFIK SUPPLY & DEMAND (RUN-RATE METHOD) */}
       <div className="mb-5 bg-white border border-gray-100 p-3 rounded-lg">
  <div className="flex items-center justify-between mb-2">
    <div>
      <p className="text-[10px] font-bold text-gray-700">Analisis Proyeksi Pasar</p>
      <p className="text-[8px] text-gray-400">Run-Rate (Hari ke-{hariBerjalan} dari {totalHariBulanIni})</p>
    </div>
    <div className="flex gap-2 text-[8px] font-semibold">
      <span className="flex items-center gap-1 text-indigo-600">
        <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full" /> Cap: {supplyCapacity.toLocaleString('id-ID')}
      </span>
      <span className="flex items-center gap-1 text-amber-600">
        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" /> Est Dmd: {marketDemandEst.toLocaleString('id-ID')}
      </span>
    </div>
  </div>

  {/* Area Grafik */}
  <div className="relative w-full h-24 bg-slate-50/50 rounded p-1 flex items-center justify-center">
    {/* Label Sumbu Y (Price/P) dan Sumbu X (Quantity/Q) */}
    <span className="absolute left-1 top-1 text-[7px] text-gray-400 font-bold">P</span>
    <span className="absolute right-1 bottom-1 text-[7px] text-gray-400 font-bold">Q</span>

    <svg className="w-full h-full" viewBox="0 0 200 80" fill="none" xmlns="http://www.w3.org/2000/svg">
  {/* Garis Sumbu X & Y */}
  <line x1="20" y1="5" x2="20" y2="70" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
  <line x1="20" y1="70" x2="190" y2="70" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
  
  {/* Garis Putus-putus Ekuilibrium Dinamis */}
  <line x1="20" y1={eqY} x2={eqX} y2={eqY} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />
  <line x1={eqX} y1={eqY} x2={eqX} y2="70" stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />

  {/* FIX PERMANEN: Kurva Supply (S) - Naik dari kiri bawah ke kanan atas */}
  <line 
    x1="35" 
    y1="60" 
    x2="175" 
    y2="15" 
    stroke="#4f46e5" 
    strokeWidth="2" 
    strokeLinecap="round" 
    opacity="0.6" 
  />
  <text x="178" y="18" fill="#4f46e5" className="text-[7px] font-bold">S</text>

  {/* FIX PERMANEN: Kurva Demand (D) - Turun dari kiri atas ke kanan bawah */}
  <line 
    x1="35" 
    y1="15" 
    x2="175" 
    y2="60" 
    stroke="#f59e0b" 
    strokeWidth="2" 
    strokeLinecap="round" 
    opacity="0.6" 
  />
  <text x="178" y="62" fill="#f59e0b" className="text-[7px] font-bold">D</text>

  {/* Titik Ekuilibrium Dinamis - Tetap meluncur bebas di ruang grafik */}
  <circle cx={eqX} cy={eqY} r="3.5" fill="#4f46e5" stroke="#ffffff" strokeWidth="1.5" />
  <text x={eqX - 2} y={eqY - 6} fill="#1e293b" className="text-[8px] font-bold">E</text>
</svg>

    {/* Badge Informasi Otomatis Dinamis */}
    <div className={`absolute bottom-1 left-7 border text-[7px] px-1.5 py-0.5 rounded font-medium ${
      activeDept?.qty >= marketDemandEst 
        ? "bg-emerald-50 text-emerald-700 border-emerald-100" 
        : "bg-blue-50 text-blue-700 border-blue-100"
    }`}>
      {activeDept?.qty >= marketDemandEst ? "Target Terpenuhi" : "On Track Menuju Target"}
    </div>
  </div>
</div>
        {/* DETAIL TIMELINE JALUR PRODUK */}
        <div className="relative pl-4 border-l-2 border-slate-100 flex flex-col gap-4 ml-1">
          {/* STEP 1: PRODUKSI */}
          <div className="relative">
            <span className="absolute -left-[21px] top-0.5 flex items-center justify-center w-3.5 h-3.5 bg-slate-100 rounded-full border border-white ring-4 ring-white z-10">
              <span className="w-1.5 h-1.5 bg-slate-500 rounded-full" />
            </span>
            <div>
              <h6 className="text-[10px] font-bold text-gray-700 flex items-center justify-between">
                <span>1. Manufaktur & Produksi Vendor</span>
                <span className="text-gray-400 font-normal text-[9px]">Selesai</span>
              </h6>
              <p className="text-[9px] text-gray-400 leading-relaxed mt-0.5">
                Barang diproduksi di pabrik rekanan pusat. Pemeriksaan kualitas (QC Pass 100%).
              </p>
            </div>
          </div>

          {/* STEP 2: DISTRIBUSI */}
          <div className="relative">
            <span className="absolute -left-[21px] top-0.5 flex items-center justify-center w-3.5 h-3.5 bg-sky-50 rounded-full border border-white ring-4 ring-white z-10">
              <span className="w-1.5 h-1.5 bg-sky-500 rounded-full" />
            </span>
            <div>
              <h6 className="text-[10px] font-bold text-gray-700 flex items-center justify-between">
                <span>2. Pengiriman & Distribusi Logistik</span>
                <span className="text-sky-600 font-bold text-[9px] bg-sky-50 px-1 rounded">In-Transit</span>
              </h6>
              <p className="text-[9px] text-gray-400 leading-relaxed mt-0.5">
                Surat Jalan dikeluarkan, barang dikirim via armada logistik menuju *Distribution Center*.
              </p>
            </div>
          </div>

          {/* STEP 3: NYAMPE TOKO */}
          <div className="relative">
            <span className="absolute -left-[21px] top-0.5 flex items-center justify-center w-3.5 h-3.5 bg-amber-50 rounded-full border border-white ring-4 ring-white z-10">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
            </span>
            <div>
              <h6 className="text-[10px] font-bold text-gray-700 flex items-center justify-between">
                <span>3. Penerimaan & Stocking Toko</span>
                <span className="text-amber-600 font-medium text-[9px]">Gudang</span>
              </h6>
              <p className="text-[9px] text-gray-400 leading-relaxed mt-0.5">
                Produk di-scan masuk sistem POS toko customer (In-Store Warehouse Ready).
              </p>
            </div>
          </div>

          {/* STEP 4: TERJUAL */}
          <div className="relative">
            <span className="absolute -left-[21px] top-0.5 flex items-center justify-center w-3.5 h-3.5 bg-indigo-50 rounded-full border border-white ring-4 ring-white z-10">
              <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full" />
            </span>
            <div>
              <h6 className="text-[10px] font-bold text-indigo-600 flex items-center justify-between">
                <span>4. Final Sales & POS Transaksi</span>
                <span className="text-emerald-600 font-bold text-[9px]">Cleared</span>
              </h6>
              <p className="text-[9px] text-gray-400 leading-relaxed mt-0.5">
                Telah terjual sebanyak <span className="font-bold text-slate-700">{activeDept.qty} unit</span> melalui kasir toko resmi dalam bulan ini.
              </p>
            </div>
          </div>
        </div>

        {/* TOMBOL LIHAT PRODUK */}
        <div className="mt-5 pl-1">
          <button
            disabled={isLoading}
            onClick={handleFetchProductData}
            className={`w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg shadow-sm transition-colors duration-200 flex items-center justify-center gap-1.5 ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Memuat Data...
              </>
            ) : (
              <>
                Lihat Produk
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </>
            )}
          </button>
        </div>

        {/* Modal Terkait */}
        <AnimatePresence>
          {isModalOpen && dbData && dbData.length > 0 && (
            <ProductListModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setDbData([]);
              }}
              activeDept={activeDept}
              dbData={dbData}
            />
          )}
        </AnimatePresence>

        {/* POPUP MODAL: ANALISA DEPARTEMEN (LIFECYCLE, AGING & SIZE METRICS) */}

      </motion.div>
    ) : (
      <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl text-gray-400 italic text-xs bg-gray-50/50">
        Menunggu data matrik termuat...
      </div>
    )}
  </div>

  </div>
</div>

<AnimatePresence>
  {analysisDept && (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop Glare */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setAnalysisDept(null)}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm"
      />

      {/* Konten Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: "spring", duration: 0.4 }}
        className="bg-white rounded-2xl shadow-xl border border-slate-100 w-full max-w-md overflow-hidden relative z-10 flex flex-col"
      >
        {/* Header Modal */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
              Real-time Inventory Analytics
            </span>
            <h4 className="text-sm font-bold text-slate-800 mt-1 flex items-center gap-1.5">
              <span className="w-1.5 h-3 bg-indigo-600 rounded-full inline-block" />
              {analysisDept.name}
            </h4>
          </div>
          <button 
            onClick={() => setAnalysisDept(null)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Isi Analisis */}
        <div className="p-4 flex flex-col gap-4">
          
          {/* SEKSYEN 1: LIFECYCLE KATEGORI PRODUK */}
          {/* SEKSYEN 1: LIFECYCLE KATEGORI PRODUK (DENGAN MINI DONUT CHART) */}
{/* SEKSYEN 1: LIFECYCLE KATEGORI PRODUK (DENGAN MINI DONUT CHART) */}
<div>
  <span className="text-[10px] text-gray-400 font-semibold block mb-2.5 uppercase tracking-wide">
    1. Komposisi Kategori & Rasio Penjualan
  </span>
  <div className="flex flex-col gap-2">
    
    {(() => {
      // AMBIL TOTAL JALUR TRANSAKSI AKTIF DARI API YANG SUDAH KITA MAPPING
      const totalSoldAll = Number(analysisDept.current_total_qty || analysisDept.qty || 0);

      // AMBIL DATA DARI QUERY DATABASE SECARA FLAT (Kombinasi fallback data dummy untuk keamanan jika null)
      const nSold = Number(analysisDept.normal_sold ?? 0);
      const hSold = Number(analysisDept.happy_price_sold ?? 0);
      const aSold = Number(analysisDept.aging_sold ?? 0);


      // KONTRIBUSI PERSENTASE SECARA DINAMIS BERDASARKAN HASIL JUAL RIIL
      const totalKategoriSold = nSold + hSold + aSold;
      const normalPct = totalKategoriSold > 0 ? Math.round((nSold / totalKategoriSold) * 100) : 65;
      const happyPct = totalKategoriSold > 0 ? Math.round((hSold / totalKategoriSold) * 100) : 20;
      const agingPct = totalKategoriSold > 0 ? Math.round((aSold / totalKategoriSold) * 100) : 15;

      const categories = [
        {
          name: "Normal",
          pct: normalPct,
          sold: nSold,
          // Sisa stok diambil proporsional atau langsung dari current_stock dibagi rata untuk demo visual
          stock: Number(analysisDept.stok_normal || 0),
          colorClass: "text-emerald-500",
          bgClass: "bg-emerald-50/40 border-emerald-100",
          badgeText: "Price Fit"
        },
        {
          name: "Happy Price",
          pct: happyPct,
          sold: hSold,
          stock: Number(analysisDept.stok_hp || 0),
          colorClass: "text-amber-500",
          bgClass: "bg-amber-50/40 border-amber-100",
          badgeText: "Promo/Diskon"
        },
        {
          name: "Aging",
          pct: agingPct,
          sold: aSold,
          stock: Number(analysisDept.stok_aging || 0),
          colorClass: "text-rose-500",
          bgClass: "bg-rose-50/40 border-rose-100",
          badgeText: "Slow Moving"
        }
      ];

      return categories.map((cat) => {
        // Logika penghitungan rasio donut chart
        const totalUnit = cat.sold + cat.stock;
        const soldPct = totalUnit > 0 ? (cat.sold / totalUnit) * 100 : 0;
        const strokeDashoffset = 100 - soldPct;

        return (
          <div key={cat.name} className={`flex items-center justify-between p-2.5 rounded-xl border ${cat.bgClass}`}>
            {/* Sisi Kiri: Nama Kategori & Kontribusi Persentase */}
            <div className="flex items-center gap-2.5">
              <div className="relative w-9 h-9 flex items-center justify-center shrink-0">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="15.9155" className="stroke-slate-200" strokeWidth="4" fill="transparent" />
                  <circle
                    cx="20" cy="20" r="15.9155"
                    className={cat.colorClass}
                    strokeWidth="4"
                    strokeDasharray="100 100"
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    fill="transparent"
                  />
                </svg>
                <span className="absolute text-[8px] font-semibold text-slate-700">{cat.pct}%</span>
              </div>

              <div>
                <span className="text-[11px] font-semibold text-slate-800 block leading-tight">{cat.name}</span>
                <span className="text-[8px] text-gray-400 font-normal block mt-0.5">{cat.badgeText}</span>
              </div>
            </div>

            {/* Sisi Kanan: Angka Distribusi Terjual vs Sisa Stok */}
            <div className="flex items-center gap-3 text-right">
              <div className="flex flex-col">
                <span className="text-[7px] text-gray-400 font-semibold uppercase tracking-wider">Terjual</span>
                <span className="text-xs font-semibold text-slate-800">
                  {cat.sold.toLocaleString('id-ID')} <span className="text-[8px] text-gray-400 font-normal">pcs</span>
                </span>
              </div>
              <div className="w-px h-5 bg-gray-200/80" />
              <div className="flex flex-col">
                <span className="text-[7px] text-gray-400 font-semibold uppercase tracking-wider">Sisa Stok</span>
                <span className="text-xs font-medium text-slate-600">
                  {cat.stock.toLocaleString('id-ID')} <span className="text-[8px] text-gray-400 font-normal">pcs</span>
                </span>
              </div>
            </div>
          </div>
        );
      });
    })()}
  </div>
</div>
          <hr className="border-slate-100" />

          {/* SEKSYEN 2: ANALISIS MUTU PRODUK & UKURAN (SIZE ANALYSIS) */}
          <div>
            <span className="text-[10px] text-gray-400 font-bold block mb-2 uppercase tracking-wide">
              2. Analisis Struktur Size & Artikel
            </span>
            
            <div className="flex flex-col gap-2">
              {/* Broken Size Row */}
              <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                  <div>
                    <span className="text-[11px] font-bold text-slate-700 block">Total Artikel Broken Size</span>
                    <span className="text-[9px] text-gray-400 block">Artikel yang kehabisan size kunci (M/L)</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-black text-slate-800">
                    {analysisDept.brokenSizeArticles || "42"} <span className="text-[9px] text-gray-400 font-normal">Art</span>
                  </span>
                </div>
              </div>

              {/* Best Selling Size Row */}
              <div className="flex items-center justify-between p-2.5 bg-indigo-50/30 rounded-xl border border-indigo-50">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-600" />
                  <div>
                    <span className="text-[11px] font-bold text-indigo-950 block">Kontribusi Size Paling Laku</span>
                    <span className="text-[9px] text-gray-400 block">Pergerakan ukuran paling dominan</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-black text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100">
                    Size {analysisDept.bestSellingSize || "L"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Rekomendasi Bisnis Cepat */}
          

        </div>

        {/* Footer Tombol Tutup */}
        <div className="p-3 bg-slate-50 border-t border-slate-100 text-right">
          <button
            onClick={() => setAnalysisDept(null)}
            className="px-4 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[11px] font-bold rounded-lg transition-colors shadow-sm"
          >
            Tutup Analisa
          </button>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>
</div>

      
    </PageWrapper>
  );
}